import React, { FunctionComponent, useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { IconSize } from '@lux/icons';
import { Flag, FlagType } from '@lux/flags';
import { PlusEarlyAccessBadge } from '@browsing/plus';
import { RootState } from '@client/redux/types';
import { trackOpenCampaignClick } from '@client/helpers/tracking';
import { buildCampaignUrl } from '@client/helpers/campaignUrl';
import {
    VALENTINE_EVENT_DELIVERY_PROMISE,
    XMAS_EVENT_DELIVERY_PROMISE,
} from '@client/config/constants';
import { getTrackingData } from '@client/redux/modules/tracking/selectors';
import { makeGetCampaignCategories } from '@client/redux/modules/myloungeMetadata/selectors';
import { Campaign } from '@shared/types/mylounge';
import { useDeliveryFlagVariant } from '@shared/abtests';
import { getCategoryTabId } from '@client/redux/modules/mylounge';
import { getCampaignGridImage } from '@client/helpers/images';

import * as S from '@client/components/mylounge/campaigns/open-campaign.styles';
import { useDeliveryLabel } from '@client/components/mylounge/hooks/use-delivery-label';
import { ShortenedDelivery } from './delivery-label.styles';
import { GridBlockDimensions } from './grid/constants';
import CampaignOverlay from './CampaignOverlay';
import CampaignLabel from './campaign-label';

const trackIsCECCampaign = {
    [XMAS_EVENT_DELIVERY_PROMISE]: 'christmas',
    [VALENTINE_EVENT_DELIVERY_PROMISE]: 'valentines',
};

interface Props {
    campaign: Campaign;
    dimension: GridBlockDimensions;
    position: string;
    campaignStyles: { [key: string]: string };
    isPlusEnabled: boolean;
}

export const OpenCampaign: FunctionComponent<Props> = ({
    campaign,
    dimension,
    campaignStyles,
    position,
    isPlusEnabled,
}) => {
    const categoryTabId = useSelector(getCategoryTabId);
    const tracking = useSelector(getTrackingData);
    const { isSpecialDeliveryBanner, showDeliveryLabel } = useDeliveryLabel(campaign);
    const getCampaignSubLineCategory = useMemo(makeGetCampaignCategories, []);
    const sublineCategory = useSelector((state: RootState) =>
        getCampaignSubLineCategory(state, campaign.campaignId),
    );
    const isNewFastDeliveryFlag: boolean = useDeliveryFlagVariant();
    const trackingFunc = useCallback(() => {
        const options = {
            categoryTabId,
            position,
            visitorID: tracking ? tracking.visitorID : null,
        };

        const component =
            trackIsCECCampaign[campaign?.deliveryPromise?.type as keyof typeof trackIsCECCampaign];

        if (component) {
            // @ts-ignore
            options.component = component;
        }

        trackOpenCampaignClick(campaign, options);
    }, [campaign, categoryTabId, position, tracking]);

    const imageUrl = useMemo(
        () => getCampaignGridImage(campaign, dimension),
        [campaign, dimension],
    );

    const campaignId = `open-${campaign.campaignId}`;

    const destination = buildCampaignUrl(campaign.campaignId, categoryTabId);
    const withDeliveryLabel = campaign?.deliveryPromise?.type;

    const coverWithoutLogo = !campaign.hasLogo;
    const isEarlyAccess = campaign.plusEarlyAccess;

    return (
        <S.Wrapper
            href={destination}
            id={campaignId}
            onClick={trackingFunc}
            data-position={position}
            src={imageUrl}
            withoutLogo={coverWithoutLogo}
            style={campaignStyles}
        >
            <S.InnerWrapper>
                {isPlusEnabled && isEarlyAccess && <PlusEarlyAccessBadge size={IconSize.S} />}

                {isNewFastDeliveryFlag && showDeliveryLabel && (
                    <ShortenedDelivery>
                        <Flag flagStyle={FlagType.NEON}>{campaign.deliveryPromise.text}</Flag>
                    </ShortenedDelivery>
                )}

                <CampaignOverlay name={campaign.name} hoverClassName="overlay" />

                <CampaignLabel
                    campaign={campaign}
                    isSpecialDeliveryBanner={isSpecialDeliveryBanner}
                    showDeliveryLabel={showDeliveryLabel}
                    categorySubline={sublineCategory}
                    hoverClassName="delivery-label"
                />

                {coverWithoutLogo && (
                    <S.Title dimension={dimension} withDeliveryLabel={withDeliveryLabel}>
                        {campaign.name}
                    </S.Title>
                )}
            </S.InnerWrapper>
        </S.Wrapper>
    );
};

export default OpenCampaign;
